<template>
  <ValidationObserver ref="userForm">
    <DialogFull
      :data="dialogSettings"
      @close="close"
      noOverflow
      v-if="dialogSettings.dialog"
    >
      <template v-slot:dialog.body>
        <div class="container">
          <div>
            <ValidationProvider
              v-slot="{ errors }"
              name="firstName"
              rules="required"
            >
              <v-text-field
                :disabled="loading"
                :error-messages="showValidationErrors ? errors : ''"
                class="mb-6 mt-6"
                v-model="firstName"
                label="First Name"
              />
            </ValidationProvider>
            <ValidationProvider
              v-slot="{ errors }"
              name="lastName"
              rules="required"
            >
              <v-text-field
                :disabled="loading"
                :error-messages="showValidationErrors ? errors : ''"
                class="mb-6 mt-2"
                v-model="lastName"
                label="Last Name"
              />
            </ValidationProvider>
            <ValidationProvider
              v-slot="{ errors }"
              name="email"
              rules="required|email"
            >
              <v-text-field
                @blur="checkEmail"
                :disabled="loading"
                :error-messages="showValidationErrors ? errors : ''"
                class="mb-2 mt-2"
                v-model="email"
                label="Email Address"
              />
            </ValidationProvider>

            <v-label>Type of User</v-label>
            <ValidationProvider
              v-slot="{ errors }"
              name="accountType"
              rules="required"
            >
              <RadioButton
                :disabled="editMode"
                v-model="accountType"
                @select="handleAccountTypeChange"
                :options="userTypes"
                :selectedValue="accountType"
                :errorMessages="showValidationErrors ? errors : ''"
                showGroupInRow
              />
            </ValidationProvider>
            <div v-if="status">
              <v-label>Status</v-label>
              <Select
                v-model="status"
                label=""
                :disabled="loading"
                autocomplete
                :items="statusItems"
                height="50"
              />
            </div>
            <div v-if="!isClient">
              <div class="font-weight-bold mb-4">Attributes</div>
              <div class="row px-4">
                <div class="checkbox-wrapper col-lg-6 col-sm-12">
                  <div
                    class="checkbox checkbox--large"
                    :class="{
                      'checkbox--large__active': isInternalCandidate
                    }"
                    @click="isInternalCandidate = !isInternalCandidate"
                  >
                    <v-checkbox
                      label="Is an internal candidate"
                      @click="isInternalCandidate = !isInternalCandidate"
                      v-model="isInternalCandidate"
                      on-icon="mdi-check"
                      color="black"
                      dense
                    />
                  </div>
                </div>
                <div class="checkbox-wrapper col-lg-6 col-sm-12">
                  <div
                    class="checkbox checkbox--large"
                    :class="{
                      'checkbox--large__active': isRedeploymentCandidate
                    }"
                    @click="isRedeploymentCandidate = !isRedeploymentCandidate"
                  >
                    <v-checkbox
                      label="Is redeployee"
                      @click="
                        isRedeploymentCandidate = !isRedeploymentCandidate
                      "
                      v-model="isRedeploymentCandidate"
                      on-icon="mdi-check"
                      color="black"
                      dense
                    />
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              <h3 class="font-weight-bold mb-4 mt-4">Roles</h3>
              <Permissioned
                section="settings.grant-user-role"
                class="d-flex flex-column"
              >
                <div
                  v-if="showValidationErrors && !selectedRoles.length"
                  class="role-error-info"
                >
                  At least one Role must be selected
                </div>
                <RoleField
                  @addRole="addRole"
                  @updateRole="updateRole"
                  @removeRole="removeRole"
                  v-for="(role, index) in roles"
                  :role="role"
                  :permission="role.permission"
                  :key="index"
                  :rootOrganisation="rootOrganisation"
                  :disabled="loading"
                  :isSelected="checkIfRoleIsAlreadySelected(role.value)"
                  :initialSelectedOrganisations="
                    getInitialSelectedOrganisations(role.value)
                  "
                  :showValidationErrors="showValidationErrors"
                />
              </Permissioned>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:dialog.action>
        <PrimaryButton
          @click.native="onSubmit"
          :loading="loading"
          v-if="!hideButton"
        >
          {{ actionName }}
        </PrimaryButton>
      </template>
    </DialogFull>
  </ValidationObserver>
</template>

<script>
import DialogFull from "@/components/common/DialogFull";
import RoleField from "@/views/settings/Users/components/RoleField";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { ROLES } from "@/views/settings/Users/constants/roles";
import { createNamespacedHelpers } from "vuex";
import { filter, findIndex, find, size, every, toString, map } from "lodash";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import Permissioned from "@/components/common/Permissioned";
import Select from "@/components/common/Select";
import { AUTH_NAMESPACE } from "@/store/modules/auth";
import { emailExistingError } from "@/utils/users";
import { USERS_NAMESPACE } from "@/store/modules/users";
import { CHECK_EXISTING_EMAIL } from "@/store/modules/users/actions";
import Vue from "vue";
import RadioButton from "@/components/common/RadioButton";
import { USER_TYPES } from "@/constants/users";

const { mapState: mapAuthState } = createNamespacedHelpers(AUTH_NAMESPACE);
const { mapActions: userMapActions } = createNamespacedHelpers(USERS_NAMESPACE);

export default {
  name: "UserDialog",
  components: {
    PrimaryButton,
    DialogFull,
    RoleField,
    Permissioned,
    ValidationObserver,
    ValidationProvider,
    Select,
    RadioButton
  },
  props: {
    isOpen: Boolean,
    loading: Boolean,
    userData: Object,
    title: String,
    actionName: String,
    userRoles: Array,
    isLoadingUserRoles: Boolean,
    editMode: Boolean
  },
  async created() {
    this.adjustFormFields();
  },
  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      roles: ROLES,
      status: "",
      isRedeploymentCandidate: false,
      isInternalCandidate: false,
      selectedRoles: [],
      showValidationErrors: false,
      statusItems: ["active", "inactive"],
      accountType: "client",
      userTypes: USER_TYPES,
      hideButton: false
    };
  },
  computed: {
    ...mapAuthState({
      rootUserData: state => state.userData
    }),
    rootOrganisation() {
      return this.rootUserData.organisation;
    },
    initialSelectedRoles() {
      return this.userRoles || [];
    },
    dialogSettings() {
      return {
        dialog: this.isOpen,
        title: this.title
      };
    },
    areRoleFieldsValid() {
      if (size(this.selectedRoles)) {
        return every(this.selectedRoles, role =>
          Boolean(size(role.organisations))
        );
      }
      return false;
    },
    isClient() {
      return this.accountType === "client";
    }
  },
  methods: {
    ...userMapActions({ checkExistingEmail: CHECK_EXISTING_EMAIL }),
    adjustFormFields() {
      if (this.userData) {
        const {
          firstName,
          lastName,
          email,
          status,
          accountType,
          isInternalCandidate,
          isRedeploymentCandidate
        } = this.userData;
        this.firstName = firstName;
        this.lastName = lastName;
        this.email = email;
        this.status = status;
        this.accountType = accountType;
        this.isInternalCandidate = isInternalCandidate;
        this.isRedeploymentCandidate = isRedeploymentCandidate;
        this.selectedRoles = this.initialSelectedRoles;
      }
      if (this.isClient) {
        this.clearUserAttributes();
      }
    },
    close() {
      this.$emit("close");
    },
    addRole(role) {
      this.selectedRoles = [...this.selectedRoles, role];
    },
    updateRole(newRoleData) {
      const updatedIndex = findIndex(this.selectedRoles, {
        name: newRoleData.name
      });
      Vue.set(this.selectedRoles, updatedIndex, newRoleData);
    },
    removeRole(nameToRemove) {
      this.selectedRoles = filter(
        this.selectedRoles,
        role => role.name !== nameToRemove
      );
    },
    async onSubmit() {
      this.showValidationErrors = true;
      const isValid = await this.$refs.userForm.validate();
      const newData = {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        roles: map(this.selectedRoles, role => ({
          ...role,
          organisations: map(role.organisations, ({ id }) => toString(id))
        })),
        accountType: this.accountType,
        isRedeploymentCandidate: this.isRedeploymentCandidate,
        isInternalCandidate: this.isInternalCandidate,
        ...(this.status && { status: this.status })
      };

      if (this.isClient && isValid && this.areRoleFieldsValid) {
        this.$emit("submit", newData);
      } else if (!this.isClient && isValid) {
        this.$emit("submit", newData);
      }
    },
    getRoleFromInitialSelectedRoles(name) {
      return find(this.initialSelectedRoles, { name });
    },
    checkIfRoleIsAlreadySelected(name) {
      return Boolean(this.getRoleFromInitialSelectedRoles(name));
    },
    getInitialSelectedOrganisations(name) {
      const selectedRole = this.getRoleFromInitialSelectedRoles(name);
      return selectedRole ? selectedRole.organisations : [];
    },
    async checkEmail() {
      if (!this.isClient) {
        const { user, isExistingEmail } = await this.checkExistingEmail(
          this.email
        );
        const id = this.userData ? this.userData.id : null;
        if (isExistingEmail && user.id !== id) {
          this.hideButton = true;
          emailExistingError(this.email);
        } else {
          this.hideButton = false;
        }
      }
    },
    clearUserAttributes() {
      this.isRedeploymentCandidate = false;
      this.isInternalCandidate = false;
    },
    handleAccountTypeChange(accountType) {
      this.accountType = accountType;
      if (this.isClient) {
        this.clearUserAttributes();
        this.selectedRoles = [];
      } else {
        this.selectedRoles = [
          { name: "worker", organisations: [this.rootOrganisation] }
        ];
      }
    }
  }
};
</script>

<style lang="scss">
.role-error-info {
  display: flex;
  border-radius: 4px;
  align-items: center;
  padding: 10px 8px;
  font-weight: bold;
  font-size: 18px;
  background-color: $error;
  color: $white;
}
</style>
